import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/** Open dialogs, oldest first: only the TOP one handles Escape and Tab (a dialog opened from a dialog). */
const openStack: symbol[] = [];

/**
 * The Home widgets' modal dialog (the gallery, and the two of an area's "Home widget" pill — `HomeWidgetControl`).
 *
 * Portalled to <body>: it opens from inside Home's canvas AND from the area header card,
 * which clips (`overflow-hidden`). A bottom sheet on a phone, centred from 640 px.
 * · Tab stays inside (a trap on the DOCUMENT, so focus that slipped out is pulled back);
 * · Escape and the scrim close it; focus returns to whatever opened it — unless the
 *   caller says `keepFocus()` first (an add hands focus to the new widget instead).
 * · One opened from another (the plan editor's "New meal") sits on top: only the top one
 *   answers Escape and Tab, so Escape closes just it and focus stays inside it.
 * ⚠️ It carries `hw-t` (Home's tokens) itself: outside the page it would resolve none.
 * · `accent`: an area's own colour for the dialog's accent (`--hw-acc`) — its focus rings, the
 *   close X's included (Supplements' amethyst). Without it, Home's cyan, as before.
 */
export function HwDialog({ title, subtitle, onClose, children, footer, initialFocus, keepFocusRef, wide = false, accent }: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** A selector inside the dialog to focus first (default: its first control). */
  initialFocus?: string;
  /** Set `.current = true` before closing to leave focus where the caller puts it. */
  keepFocusRef?: React.MutableRefObject<boolean>;
  wide?: boolean;
  /** The area's accent for the dialog's focus rings, the close X's too (default: Home's `--hw-acc`). */
  accent?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const opener = useRef<Element | null>(typeof document !== 'undefined' ? document.activeElement : null);
  const me = useRef(Symbol('dialog'));

  useEffect(() => {
    const id = me.current;
    openStack.push(id);
    return () => { const i = openStack.indexOf(id); if (i >= 0) openStack.splice(i, 1); };
  }, []);

  useEffect(() => {
    const el = ref.current;
    const first = (initialFocus && el?.querySelector<HTMLElement>(initialFocus)) || el?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, [initialFocus]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = ref.current;
      if (!el || openStack[openStack.length - 1] !== me.current) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1], active = document.activeElement;
      if (!el.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    // Capture: Escape here must close the dialog, not also leave the page's edit mode.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => () => {
    if (keepFocusRef?.current) return;
    const el = opener.current;
    if (el instanceof HTMLElement && el.isConnected) el.focus();
  }, [keepFocusRef]);

  return createPortal(
    <div className="hw-t hw-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className="hw-dlg" data-wide={wide ? '' : undefined} role="dialog" aria-modal="true" aria-labelledby={titleId}
        style={accent ? { '--hw-acc': accent } as React.CSSProperties : undefined}>
        <div className="hw-dlg-head">
          <div className="min-w-0">
            <h2 id={titleId} className="hw-dlg-title">{title}</h2>
            {subtitle && <p className="hw-dlg-sub">{subtitle}</p>}
          </div>
          <button type="button" className="hw-icon" aria-label="Close" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="hw-dlg-body">{children}</div>
        {footer && <div className="hw-dlg-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
